import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, UserPlus, User, Phone } from "lucide-react";
import AlertModal from "../components/AlertModal";
import { useAuth } from "../context/AuthContext";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { BASE_URL } from "../config";

const Register = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });
  
  const { register, redirectAfterAuth, setRedirectAfterAuth } = useAuth();
  const navigate = useNavigate();

  const signUpWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Get user info from Google
        const { data } = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );

        // Send to backend for login/signup
        await axios.post(`${BASE_URL}/api/auth/google`, data, {
          withCredentials: true, // so cookies are set
        });

        window.location.href = "/dashboard"; // or use navigate
      } catch (err) {
        console.error("Google signup error:", err);
      }
    },
    onError: (error) => console.log("SignUp Failed:", error),
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isStrongPassword = (password) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) return setError("Full name is required.");
    if (!form.phone.trim()) return setError("Phone number is required.");
    if (!isValidEmail(form.email)) return setError("Please enter a valid email address.");
    if (!isStrongPassword(form.password)) return setError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
    if (form.password !== form.confirmPassword) return setError("Passwords do not match.");
    if (!acceptTerms) return setError("You must accept the Terms and Conditions.");

    setLoading(true); // 🔹 Start loading

    try {
      await register({
        name: form.name,
        phone: form.phone,
        email: form.email,
        password: form.password,
      });

      setSuccess("Registration successful! Redirecting...");
      setError("");

      setTimeout(() => {
        navigate(redirectAfterAuth || "/verify-notice", { state: { email: form.email }, replace: true });
        setRedirectAfterAuth(null);
      }, 1500);

    } catch (err) {
      console.error("Registration error:", err);
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false); // 🔹 Stop loading no matter what
    }
  };

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <>
      <div className="min-h-screen flex items-center justify-center px-4 bg-gray-100">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6 flex items-center justify-center gap-2">
            Register
          </h2>

          {/* Notifications */}
          {error && (
            <div className="bg-red-100 text-red-600 text-sm px-3 py-2 rounded mb-3">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded mb-3">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            {/* Full Name */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <User className="w-4 h-4 text-gray-500" /> Full Name
              </label>
              <input
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Phone Number */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Phone className="w-4 h-4 text-gray-500" /> Phone Number
              </label>
              <input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Email */}
            <div className="w-full md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Mail className="w-4 h-4 text-gray-500" /> Email Address
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Password */}
            <div className="w-full md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Lock className="w-4 h-4 text-gray-500" /> Password
              </label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="w-full md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Lock className="w-4 h-4 text-gray-500" /> Confirm Password
              </label>
              <div className="relative">
                <input
                  name="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
                >
                  {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Terms Checkbox */}
            <div className="w-full md:col-span-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                id="terms"
                checked={acceptTerms}
                onChange={() => setAcceptTerms(!acceptTerms)}
                required
                className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <label htmlFor="terms" className="text-gray-600">
                I accept the{" "}
                <Link to="/terms" className="text-blue-600 hover:underline">
                  Terms and Conditions
                </Link>.
              </label>
            </div>

            {/* Register Button */}
            <div className="w-full md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className={`w-full font-semibold py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200 
                  ${loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    Registering...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" /> Register
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative text-center mb-4">
              <span className="bg-white px-2 text-gray-400 text-sm">or</span>
              <div className="absolute top-1/2 left-0 right-0 border-t border-gray-300 -z-10"></div>
            </div>
            <button
              type="button"
              className="w-full border border-gray-300 hover:bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-md flex items-center justify-center gap-2 transition duration-200"
              onClick={() => signUpWithGoogle()}
            >
              <img src="/images/icons/google-logo.svg" alt="Google" className="w-5 h-5" />
              Sign Up with Google
            </button>
          </div>

          <p className="mt-6 text-sm text-center text-gray-600">
            Already have an account?{" "}
            <Link 
            to="/signin" 
            className="text-blue-600 hover:underline"
            onClick={() => {
              const redirect = localStorage.getItem("redirectAfterAuth");
              if (redirect) localStorage.setItem("redirectAfterAuth", redirect);
            }}
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.open}
        onClose={() => setAlert({ open: false, message: "", type: "info" })}
        message={alert.message}
        type={alert.type}
      />
    </>
  );
};

export default Register;
